import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    Snackbar,
    Alert,
    Fade,
    IconButton,
    InputAdornment,
    GlobalStyles,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Person as PersonIcon,
    Lock as LockIcon,
    Login as LoginIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import Logo from '../../assests/logo.png';
import { useAuthContext } from '../../shared/contexts';

export const Login = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { isAuthenticated, login, setAccessToken } = useAuthContext();

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [passwordError, setPasswordError] = useState('');
    const [usernameError, setUsernameError] = useState('');

    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');

    const [openErrorToast, setOpenErrorToast] = useState(false);

    const validateForm = () => {
        let hasError = false;

        if (!username.trim()) {
            setUsernameError('Informe o e-mail.');
            hasError = true;
        }

        if (!password.trim()) {
            setPasswordError('Informe a senha.');
            hasError = true;
        }

        return !hasError;
    };

    const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            const result = await login(username, password);

            if (result instanceof Error || !result || !result.token) {
                setOpenErrorToast(true);
                return;
            }

            setAccessToken(result.token);

            setUsername('');
            setPassword('');

            navigate('/home');
        } catch {
            setOpenErrorToast(true);
        } finally {
            setIsLoading(false);
        }
    };

    if (isAuthenticated) {
        return <Navigate to="/home" />;
    }

    return (
        <>
            <GlobalStyles
                styles={{
                    html: {
                        margin: 0,
                        padding: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    },
                    body: {
                        margin: 0,
                        padding: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    },
                    '#root': {
                        margin: 0,
                        padding: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    },
                }}
            />

            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    width: '100vw',
                    height: '100dvh',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.default,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.primary.dark} 100%)`,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: { xs: 3, md: 6 },
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `
                                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.12) 0%, transparent 45%),
                                radial-gradient(circle at 80% 70%, rgba(255,183,27,0.30) 0%, transparent 45%)
                            `,
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Fade in timeout={700}>
                        <Box
                            sx={{
                                width: 150,
                                height: 150,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.97)',
                                border: '3px solid rgba(255,255,255,0.22)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 4,
                                position: 'relative',
                                zIndex: 1,
                                boxShadow: '0 18px 45px rgba(0,0,0,0.16)',
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Admin"
                                style={{
                                    width: '72%',
                                    height: '72%',
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>
                    </Fade>

                    <Fade in timeout={900}>
                        <Box
                            sx={{
                                textAlign: 'center',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            <Typography
                                component="h1"
                                variant={isMobile ? 'h4' : 'h3'}
                                sx={{
                                    color: '#fff',
                                    fontWeight: 800,
                                    mb: 1.5,
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                QR Mania Admin
                            </Typography>

                            <Typography
                                variant={isMobile ? 'body1' : 'h6'}
                                sx={{
                                    color: 'rgba(255,255,255,0.92)',
                                    fontWeight: 400,
                                }}
                            >
                                Painel Administrativo
                            </Typography>
                        </Box>
                    </Fade>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.background.paper,
                        px: { xs: 3, sm: 6, md: 8 },
                        overflow: 'hidden',
                    }}
                >
                    <Fade in timeout={600}>
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                width: '100%',
                                maxWidth: 440,
                            }}
                        >
                            <Typography
                                component="h2"
                                variant="h4"
                                sx={{
                                    mb: 1,
                                    fontWeight: 800,
                                    color: theme.palette.text.primary,
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                Bem-vindo
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 5,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Faça login para acessar sua conta
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 600,
                                    }}
                                >
                                    E-mail
                                </Typography>

                                <TextField
                                    fullWidth
                                    type="email"
                                    placeholder="Digite seu e-mail"
                                    value={username}
                                    disabled={isLoading}
                                    error={!!usernameError}
                                    helperText={usernameError}
                                    onKeyDown={() => setUsernameError('')}
                                    onChange={(e) => setUsername(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mb: 1,
                                        fontWeight: 600,
                                    }}
                                >
                                    Senha
                                </Typography>

                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Digite sua senha"
                                    value={password}
                                    disabled={isLoading}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    onKeyDown={() => setPasswordError('')}
                                    onChange={(e) => setPassword(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPassword(!showPassword)
                                                    }
                                                >
                                                    {showPassword
                                                        ? <VisibilityOffIcon />
                                                        : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                        },
                                    }}
                                />
                            </Box>

                            {/* <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mb: 4,
                                }}
                            >
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={() => navigate('/forgotPassword')}
                                    sx={{
                                        border: 0,
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Esqueceu a senha?
                                </Typography>
                            </Box> */}

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={isLoading}
                                startIcon={
                                    isLoading
                                        ? undefined
                                        : <LoginIcon />
                                }
                                sx={{
                                    py: 2,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    mb: 4,
                                    borderRadius: 1,
                                    '&:hover': {
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress
                                        variant="indeterminate"
                                        color="inherit"
                                        size={22}
                                    />
                                ) : (
                                    'Entrar'
                                )}
                            </Button>

                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Painel Administrativo QR Mania
                            </Typography>
                        </Box>
                    </Fade>
                </Box>

                <Snackbar
                    open={openErrorToast}
                    autoHideDuration={4000}
                    onClose={() => setOpenErrorToast(false)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Alert
                        onClose={() => setOpenErrorToast(false)}
                        severity="error"
                        sx={{ width: '100%' }}
                    >
                        Dados de login inválidos. Verifique e tente novamente.
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};
